import { useMutation } from '@apollo/client';
import { useLogEvent } from 'hooks/useLogEvent';
import useMessages from 'hooks/useMessages';
import { UPDATE_ACTIVITY } from 'graphql/secure/mutations/activity.mutations';
import {
  CREATE_CHAIN_ACTIVITY,
  CREATE_CHAIN_STEPS,
  CREATE_SUB_CHAIN_FROM_STEP_ACTIVITIES,
  DELETE_CHAIN_ACTIVITY,
  DELETE_CHAIN_STEP,
  DELETE_COMPONENT_CHAIN,
  DUPLICATE_CHAIN,
  IMPORT_CHAINS,
  INITIALISE_CHAIN_MAPPING,
  REMOVE_SUB_CHAIN,
  REORDER_SUB_CHAINS,
  UPDATE_CHAIN_ACTIVITY,
  UPDATE_CHAIN_STEP,
} from 'graphql/secure/mutations/chain.mutations';
import { UPDATE_VISIBILITY_SETTINGS } from 'graphql/secure/mutations/supplyChai.mutations';
import {
  ICreateChainActivityInput,
  ICreateChainStepsInput,
  ICreateSubChainFromStepActivitiesInput,
  IDuplicateChainInput,
  IUpdateChainActivityInput,
  IUpdateChainStepInput,
} from 'graphql/secure/mutations/types/chains-mutation.types';
import { GET_CHAINS } from 'graphql/secure/queries';
import { useNavigate } from 'react-router-dom';
import { IActivity } from 'FeatureModules/Activity/types/activity.types';
import { IChainResponse } from 'FeatureModules/ComponentChain/types/chain.types';
import { ELanguage } from 'DesignSystem/types/language.types';
import { TUpdateActivityInput } from 'graphql/secure/mutations/types/activity-mutation.types';

const useSupplyChainActions = () => {
  const { logEvent } = useLogEvent();
  const { setSuccessMessage, setErrorMessage } = useMessages();
  const navigate = useNavigate();

  const [createSteps, { loading: createStepLoading }] = useMutation<
    { createChainSteps: IChainResponse },
    { input: ICreateChainStepsInput }
  >(CREATE_CHAIN_STEPS, {
    onCompleted: () => {
      setSuccessMessage('Success! Your supply chain was updated.');
    },
    onError: e => {
      setErrorMessage('Something went wrong updating your supply chain');
    },
  });

  const [updateStep, { loading: updateStepLoading }] = useMutation<
    { updateChainStep: IChainResponse },
    { input: IUpdateChainStepInput }
  >(UPDATE_CHAIN_STEP, {
    onCompleted: () => {
      setSuccessMessage('Success! Your supply chain was updated.');
    },
    onError: () => {
      setErrorMessage('Something went wrong updating your supply chain');
    },
  });

  const [deleteStep, { loading: deleteStepLoading }] = useMutation<
    { deleteChainStep: IChainResponse },
    { id: string }
  >(DELETE_CHAIN_STEP, {
    onCompleted: () => {
      setSuccessMessage('Success! Your supply chain was updated.');
    },
    onError: () => {
      setErrorMessage('Something went wrong updating your supply chain');
    },
  });

  const [importSubChains, { loading: importSubChainsLoading }] = useMutation<
    { importChains: IChainResponse },
    { id: string; importChainIds: string[] }
  >(IMPORT_CHAINS, {
    onCompleted: () => {
      logEvent('SUB_CHAIN_IMPORTED');
      setSuccessMessage('Success! Your supply chain was updated.');
    },
    onError: () => {
      setErrorMessage('Something went wrong updating your supply chain');
    },
  });

  const [removeSubChain, { loading: removeSubChainLoading }] = useMutation<
    { removeSubChain: IChainResponse },
    {
      id: string;
    }
  >(REMOVE_SUB_CHAIN, {
    onCompleted: () => {
      logEvent('SUB_CHAIN_REMOVED');
      setSuccessMessage('Success! Your supply sub chain was deleted.');
    },
    onError: () => {
      setErrorMessage('Something went wrong deleting your supply chain');
    },
  });

  const [reorderSubChains, { loading: reorderSubChainsLoading }] = useMutation<
    { reorderSubChains: IChainResponse },
    { id: string; subChainIdsOrdered: string[] }
  >(REORDER_SUB_CHAINS, {
    onCompleted: () => {
      logEvent('SUB_CHAIN_REORDERED');
      setSuccessMessage('Success! Your reordered the supply sub chains.');
    },
    onError: () => {
      setErrorMessage('Something went reordering the supply sub chain');
    },
  });

  const [deleteChain, { loading: deleteChainLoading }] = useMutation<
    { deleteChain: { id: string } },
    { id: string }
  >(DELETE_COMPONENT_CHAIN, {
    onCompleted: () => {
      setSuccessMessage('Success! Your supply chain was deleted.');
    },
    onError: () => {
      setErrorMessage('Something went wrong deleting your supply chain');
    },
    // We need to refetch everything here since the isUsed field could change for other chains
    refetchQueries: [GET_CHAINS],
  });

  const [copySupplyChain, { loading: copySupplyChainLoading }] = useMutation<
    { copyChain: IChainResponse },
    { input: IDuplicateChainInput }
  >(DUPLICATE_CHAIN, {
    onCompleted: () => {
      setSuccessMessage('Success! Your supply chain was created.');
    },
    onError: () => {
      setErrorMessage('Something went wrong copying your supply chain');
    },
  });

  const [initialiseChainMapping, { loading: initialiseChainMappingLoading }] = useMutation<
    { initialiseChainMapping: IChainResponse },
    { id: string }
  >(INITIALISE_CHAIN_MAPPING, {
    onCompleted: ({
      initialiseChainMapping: {
        chain: { id, productId },
      },
    }) => {
      navigate(`/supply-chains/active/${productId}/${id}/editor`);
    },
    onError: () => {
      setErrorMessage('Something went wrong initializing your supply chain');
    },
  });

  const [createActivity, { loading: createActivityLoading }] = useMutation<
    { createChainActivity: IChainResponse },
    { input: ICreateChainActivityInput }
  >(CREATE_CHAIN_ACTIVITY, {
    onCompleted: () => {
      setSuccessMessage('Success! Your activity was created.');
    },
    onError: () => {
      setErrorMessage('Something went wrong creating your activity');
    },
  });

  const [updateActivity, { loading: updateActivityLoading }] = useMutation<
    { updateChainActivity: IChainResponse },
    {
      id: string;
      input: IUpdateChainActivityInput;
    }
  >(UPDATE_CHAIN_ACTIVITY, {
    onCompleted: () => {
      setSuccessMessage('Success! Your activity was changed.');
    },
    onError: () => {
      setErrorMessage('Something went wrong updating your activity');
    },
  });

  const [deleteActivity, { loading: deleteActivityLoading }] = useMutation<
    { deleteChainActivity: IChainResponse },
    { id: string }
  >(DELETE_CHAIN_ACTIVITY, {
    onCompleted: () => {
      setSuccessMessage('Success! Your activity was deleted.');
    },
    onError: () => {
      setErrorMessage('Something went wrong deleting your activity');
    },
  });

  const [updateActivityPartner, { loading: updateActivityPartnerLoading }] = useMutation<
    { updateActivity: { activity: IActivity } },
    { id: string; lang: ELanguage; input: TUpdateActivityInput }
  >(UPDATE_ACTIVITY, {
    onCompleted: () => {
      setSuccessMessage('Partner successfully updated');
    },
    onError: () => setErrorMessage('There was an error updating your partner'),
  });

  const [updateVisibilitySettings, { loading: updateVisibilitySettingsLoading }] = useMutation(
    UPDATE_VISIBILITY_SETTINGS,
    {
      onCompleted: () => {
        setSuccessMessage('Success! Your visibility settings have been changed.');
      },
      onError: () => {
        setErrorMessage('Something went wrong updating your visibility settings');
      },
    }
  );

  const [createSubChainFromStepActivities, { loading: createSubChainFromStepActivitiesLoading }] =
    useMutation<
      { createSubChainFromStepActivities: IChainResponse },
      { input: ICreateSubChainFromStepActivitiesInput }
    >(CREATE_SUB_CHAIN_FROM_STEP_ACTIVITIES, {
      onCompleted: () => {
        logEvent('SUB_CHAIN_FROM_STEP_ACTIVITY_CREATED');
        setSuccessMessage('Success! Your supply chain was updated.');
      },
      onError: event => {
        if (event.message === 'CHAIN_IMPORT_TOO_DEEP') {
          setErrorMessage(
            'The sub chain you are trying to create is too deep. Please create a sub chain from the parent chain.'
          );
        } else {
          setErrorMessage('Something went wrong updating your supply chain');
        }
      },
    });

  const loading =
    createStepLoading ||
    updateStepLoading ||
    deleteStepLoading ||
    importSubChainsLoading ||
    removeSubChainLoading ||
    deleteChainLoading ||
    reorderSubChainsLoading ||
    initialiseChainMappingLoading ||
    createActivityLoading ||
    updateActivityLoading ||
    deleteActivityLoading ||
    updateActivityPartnerLoading ||
    updateVisibilitySettingsLoading ||
    createSubChainFromStepActivitiesLoading ||
    copySupplyChainLoading;

  return {
    loading,
    updateStep,
    createSteps,
    deleteStep,
    removeSubChain,
    importSubChains,
    deleteChain,
    createActivity,
    updateActivity,
    deleteActivity,
    reorderSubChains,
    updateActivityPartner,
    initialiseChainMapping,
    updateVisibilitySettings,
    createSubChainFromStepActivities,
    copySupplyChain,
  };
};

export default useSupplyChainActions;
